// src/oura/components/docs/create/RadioList.tsx
//
// A pick-one list inside a drawer block, as Pick a code draws it: a 44px row
// per option with a round mark, driven as a radio group (one tab stop, the
// arrow keys move and choose). An option can be shut: it stays on the list,
// greyed, with its reason, and the arrows pass it by. The picked option can
// open a field under itself (`extra`).

import type { ReactNode } from "react";
import { cn } from "../../../lib/cn";
import { useRadioGroup } from "../../../lib/useRadioGroup";
import { FOCUS_RING, TYPE } from "@oura/ui";

export interface RadioOption {
    key: string;
    label: ReactNode;
    /** 1 line under the label: chips, a path, a reason. */
    sub?: ReactNode;
    /** Shut: shown greyed with its `sub`, never picked. */
    disabled?: boolean;
    /** Opens under the option while it is picked (a link field, a file list). */
    extra?: ReactNode;
}

function Mark({ on, off }: { on: boolean; off?: boolean }) {
    return (
        <span
            aria-hidden
            className={cn(
                "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full ring-1 ring-inset",
                on ? "bg-blue-600 ring-blue-600" : off ? "bg-gray-50 ring-gray-200" : "bg-white ring-gray-300",
            )}
        >
            {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
        </span>
    );
}

export function RadioList({
    labelId,
    options,
    value,
    onPick,
}: {
    /** The id of the visible heading that names the group. */
    labelId: string;
    options: readonly RadioOption[];
    value: string | null;
    onPick: (key: string) => void;
}) {
    const open = options.filter((o) => !o.disabled);
    const keys = open.map((o) => o.key);
    const radio = useRadioGroup(keys, value ?? undefined, onPick);
    return (
        <div role="radiogroup" aria-labelledby={labelId} className="mt-2">
            {options.map((o) => {
                if (o.disabled) {
                    return (
                        <div key={o.key} role="radio" aria-checked={false} aria-disabled className="flex min-h-11 w-full items-start gap-3 rounded-xl px-3 py-2.5">
                            <Mark on={false} off />
                            <span className="min-w-0 flex-1">
                                <span className={cn(TYPE.tb, "block break-words text-gray-400")}>{o.label}</span>
                                {o.sub && <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>{o.sub}</span>}
                            </span>
                        </div>
                    );
                }
                const i = keys.indexOf(o.key);
                const on = o.key === value;
                return (
                    <div key={o.key}>
                        <button
                            type="button"
                            {...radio(i)}
                            className={cn(
                                "flex min-h-11 w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors motion-reduce:transition-none",
                                on ? "bg-blue-50" : "hover:bg-gray-50",
                                FOCUS_RING,
                            )}
                        >
                            <Mark on={on} />
                            <span className="min-w-0 flex-1">
                                <span className={cn(TYPE.tb, "block break-words", on ? "text-gray-900" : "text-gray-700")}>{o.label}</span>
                                {o.sub && <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>{o.sub}</span>}
                            </span>
                        </button>
                        {on && o.extra && <div className="pb-2 pl-10 pr-3 pt-1">{o.extra}</div>}
                    </div>
                );
            })}
        </div>
    );
}
